import { CommonModule, DOCUMENT, NgOptimizedImage } from '@angular/common';
import { Component, computed, Inject, inject, NgZone, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterLinkActive } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { IonicModule, ModalController, NavController, Platform } from '@ionic/angular';
import { Screenshot } from 'capacitor-screenshot';
import moment from 'moment';
import { catchError, of, Subscription, take, tap } from 'rxjs';
import { register } from 'swiper/element/bundle';
import { environment } from '../environments/environment';
import { OIDC_SERVICE_INJECTION_TOKEN } from '../main';
import { CrtNotificationSousType, NotificationCRT } from './crt/providers/crt-notification-handler';
import { CrtToastCloseType, CrtTypeCommToast } from './crt/providers/toast.service';
import { LrService } from './crt/sites/providers/lr.service';
import { SiteService } from './crt/sites/providers/site.service';
import { DysfonctionnementPage } from './dysfonctionnement/dysfonctionnement.page';
import { NewsIndispoDetailComponent } from './news-indispos/news-indispo-detail/news-indispo-detail.component';
import { AuthentificationType, OidcSecurityService } from './oidc/oidc-security.service';
import { ChronoComponent } from './shared/components/chrono/chrono.component';
import { FooterNewVersionComponent } from './shared/components/footer-new-version/footer-new-version.component';
import { KpisTechnicienModalService } from './shared/components/kpis-technicien/kpis-technicien-modal.service';
import { NetworkStatusComponent } from './shared/components/network-status/network-status.component';
import { ActiviteInterneService } from './shared/providers/activite-interne/activite-interne.service';
import { AnalyticsProvider } from './shared/providers/analytics/analytics';
import { AuthService } from './shared/providers/auth/authentification.service';
import { UserService } from './shared/providers/auth/user.service';
import { ChronosProvider } from './shared/providers/chronos/chronos.provider';
import { UserContextService } from './shared/providers/context/user-context.service';
import { DeviceServices } from './shared/providers/device/device.service';
import { EquipesProvider } from './shared/providers/equipes/equipes.provider';
import { Events } from './shared/providers/events/events.provider';
import { IconsProvider } from './shared/providers/icons/icons.provider';
import { MenuItem, MenuPage, MenuService } from './shared/providers/menu.service';
import { NetworkProvider, NetworkStatus } from './shared/providers/network/network';
import { NewsIndisposService } from './shared/providers/news-indispos/news-indispos.service';
import { MultiTechNotification } from './shared/providers/notifications-alert/notifications-alert.provider';
import { CommonNotificationHandler } from './shared/providers/notifications-handler/common-notification-handler';
import { COMPONENT_TITLE } from './shared/utils/component-name';
import { StartupService } from './shared/providers/startup.service';
import { DactechDeviceInfo } from './shared/providers/device/device.model';
import { TranslateService } from '@ngx-translate/core';
import { RequestsOfflineProvider } from './shared/providers/requests/request-offline.provider';
import { CheckPasswordService } from './shared/providers/auth/check-password.service';
import { GeolocConsentModalService } from './shared/components/cmp/geoloc-consent-modal.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActiviteEnCoursService } from './shared/components/activite-en-cours/activite-en-cours.service';
import { BandeauActiviteComponent } from './shared/components/activite-en-cours/bandeau-activite/bandeau-activite.component';
import { ChronoActiviteService } from './shared/providers/chrono-activite/chrono-activite.service';
import { FeedbackComponent } from './shared/components/feedback/feedback.component';
import {
  isToggleConsentGeolocEnabled,
  isTogglePasswordExpiryEnabled,
  isTogglePtiButtonSOSEnabled,
} from './shared/utils/toggle-utils';
import { SOSComponent } from './shared/components/sos/sos.component';
import { LoggerFactory } from './shared/providers/logger/logger-factory.service';
import { DemarrerOtService } from './mnt/ot/demarrer-ot.service';
import { DemarrerActiviteInterneService } from './shared/providers/activite-interne/demarrer-activite-interne.service';
import { PortailSecuriteProvider } from './shared/providers/securite/portail-securite.provider';

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    IonicModule,
    CommonModule,
    RouterLinkActive,
    NetworkStatusComponent,
    NgOptimizedImage,
    ChronoComponent,
    FooterNewVersionComponent,
    BandeauActiviteComponent,
    FeedbackComponent,
    SOSComponent,
  ],
  standalone: true,
})
export class AppComponent implements OnInit, OnDestroy {
  protected readonly userService: UserService = inject(UserService);
  private readonly kpisTechnicienService = inject(KpisTechnicienModalService);
  private readonly equipesProvider = inject(EquipesProvider);
  private readonly zone: NgZone = inject(NgZone);
  private readonly router: Router = inject(Router);
  private readonly networkProvider: NetworkProvider = inject(NetworkProvider);
  private readonly document_: Document;
  private readonly authService: AuthService = inject(AuthService);
  private readonly deviceServices: DeviceServices = inject(DeviceServices);
  private readonly analyticsProvider: AnalyticsProvider = inject(AnalyticsProvider);
  private readonly events: Events = inject(Events);
  private readonly oidcSecurityService: OidcSecurityService = inject(OIDC_SERVICE_INJECTION_TOKEN);
  private readonly chronosProvider: ChronosProvider = inject(ChronosProvider);
  private readonly startupService: StartupService = inject(StartupService);
  private readonly commonNotificationHandler = inject(CommonNotificationHandler);
  private readonly siteService = inject(SiteService);
  private readonly newsIndisposService = inject(NewsIndisposService);
  private readonly logger = inject(LoggerFactory).getInstance(this, true);
  private readonly lrService = inject(LrService);
  private readonly iconsProvider = inject(IconsProvider);
  private readonly navCtrl = inject(NavController);
  private readonly userContextService = inject(UserContextService);
  private readonly geolocConsentModalService = inject(GeolocConsentModalService);
  private readonly checkPasswordService: CheckPasswordService = inject(CheckPasswordService);
  private readonly activiteEnCoursService: ActiviteEnCoursService = inject(ActiviteEnCoursService);
  private readonly chronoActiviteService: ChronoActiviteService = inject(ChronoActiviteService);
  private readonly demarrerOtService: DemarrerOtService = inject(DemarrerOtService);
  private readonly demarrerActiviteInterneService: DemarrerActiviteInterneService =
    inject(DemarrerActiviteInterneService);
  /**
   * Lien du menu actif
   */
  active = MenuPage.Home;
  activeLink: MenuItem;

  /**
   * Listes des liens du menu
   */
  get links(): MenuItem[] {
    return this.menuService.getMenu();
  }
  NetworkStatusEnum = NetworkStatus;
  networkStatus = signal(NetworkStatus.ONLINE);
  dbCreatedSubscription: Subscription;
  authSubscription: Subscription;
  deeplinkAuthSubscription: Subscription;
  fullAppVersion: string = environment.fullAppVersion;

  hasChrono = signal(false);
  showChrono: boolean;

  authenticated: boolean = false;
  isMntDiffus: boolean = false;

  activiteEncours = toSignal(this.activiteEnCoursService.activiteEnCours$);
  hasActiviteEncours = computed(() => {
    return this.activiteEncours() !== null;
  });

  chronoEnCours = toSignal(this.chronoActiviteService.chronoActiviteEnCours$);

  contentHeight = computed(() => {
    let baseHeight = '100%';
    if (this.networkStatus() !== this.NetworkStatusEnum.ONLINE) {
      baseHeight = '95%';
    }
    return 'calc(' + baseHeight + ')';
  });

  constructor(
    private platform: Platform,
    private menuService: MenuService,
    private modalCtrl: ModalController,
    private requestsOfflineProvider: RequestsOfflineProvider,
    private activiteInterneService: ActiviteInterneService,
    private translateService: TranslateService,
    private portailSecuriteProvider: PortailSecuriteProvider,
    @Inject(DOCUMENT) document_: Document
  ) {
    this.document_ = document_;
  }

  ngOnInit() {
    this.translateService.use('fr');
    this.iconsProvider.registerIcons();
    moment.locale('fr');

    this.platform.ready().then(() => {
      if (Capacitor.isNativePlatform()) {
        StatusBar.setOverlaysWebView({ overlay: true });
        StatusBar.setBackgroundColor({ color: '#000000' });
        StatusBar.setStyle({ style: Style.Dark });
        SplashScreen.show({
          showDuration: 2000,
          autoHide: true,
        });
      }

      this.networkProvider.initNetworkStatus();
      this.networkProvider.networkStatusSubject
        .pipe(
          tap((networkStatus) => {
            this.networkStatus.set(networkStatus);
            this.zone.run(() => {});
          })
        )
        .subscribe();
    });

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const match = event.url.match(/code=([^&]+)&state=([^&]+)/);
        if (match) {
          this.oidcSecurityService.getRedirectUrl('sensible').subscribe((redirectUrl) => {
            const type: AuthentificationType = event.url.startsWith(redirectUrl) ? 'sensible' : 'normal';
            this.deeplinkAuthSubscription = this.authService.authenticate(type, event.url).subscribe();
          });
        }
      });
    });

    this.authSubscription = this.authService.authenticate('normal').subscribe();

    this.updateDisabledMenuItem();
    this.events.subscribe('authentification:firstLoginSuccessful', async () => {
      this.startupService.configureDatadogForUser();
      await this.startupService.loadConfigAndUserData();
      if (isTogglePasswordExpiryEnabled()) {
        await this.checkPasswordService.checkPasswordExpiration();
      }
      await this.equipesProvider.refreshEquipes(this.userService.getId());
      await this.kpisTechnicienService.displayKpis();

      const activiteEnCours = await this.activiteEnCoursService.getActiviteEnCours();
      if (activiteEnCours) {
        if (activiteEnCours.type === 'ot') {
          await this.demarrerOtService.reloadOt();
        } else {
          await this.demarrerActiviteInterneService.reloadActiviteInterne();
        }
      }

      await this.geolocConsentModalService.displayModalIfNoConsent(this.userService.getId());
      await this.enrollUserToFirebase();
      await this.sendDeviceInfo();
      if (this.userService.hasCRTRights()) {
        this.newsIndisposService.getNewsIndispos(true, false).pipe(take(1)).subscribe();
      } else {
        this.newsIndisposService.deleteStoredNewIndispo();
      }
      this.startupService.synchroDonnees();
    });

    this.events.subscribe('refreshTab', () => {
      this.refreshTab();
    });

    this.router.events.subscribe((evt) => {
      if (evt instanceof NavigationEnd) {
        this.updateCurrentComponent(this.router.routerState.snapshot.root);
        this.processViewChanged();
      }
    });

    this.refreshTab();

    this.networkProvider.networkStatusSubject
      .pipe(
        tap(async (networkStatus) => {
          if (networkStatus === NetworkStatus.ONLINE) {
            this.activiteInterneService.synchroCrNonEnvoye().then();
            this.portailSecuriteProvider.retryPostTopIntervention().then();
            this.requestsOfflineProvider
              .processRequests()
              .then(() => {})
              .catch((err) => {
                this.logger.error(err, 'Erreur lors du traitement des requêtes offlines');
              });
          } else if (networkStatus === NetworkStatus.OFFLINE || networkStatus === NetworkStatus.DEGRADED) {
            this.requestsOfflineProvider
              .stopRequests()
              .then(() => {})
              .catch((err) => {
                this.logger.error(err, "Erreur lors de l'arrêt du traiement des requêtes offlines");
              });
          }
          this.checkLinksDisabledNetwork(
            networkStatus === NetworkStatus.ONLINE || networkStatus === NetworkStatus.DEGRADED
          );
          this.networkStatus.set(networkStatus);
        })
      )
      .subscribe();

    this.platform.pause.subscribe(() => {
      this.logger.debug("Mise en pause de l'application");
      this.networkProvider.addHistory('**** APP PAUSED ****');
      this.events.publish('appPaused');
    });
  }

  private async sendDeviceInfo() {
    if (Capacitor.isNativePlatform()) {
      let device_infos: DactechDeviceInfo = this.deviceServices.getInfos();
      device_infos.id_intervenant = this.userService.getId();
      await this.analyticsProvider.sendDeviceInfo(device_infos);
    } else {
      let infos: DactechDeviceInfo = {
        device_os_version: 'D@c Tech PC',
        device_app_version: environment.appVersion,
        device_manufacturer: '',
        device_model: '',
        id_intervenant: this.userService.getId(),
        date_creation: new Date().toISOString(),
      };
      await this.analyticsProvider.sendDeviceInfo(infos);
    }
  }

  private updateCurrentComponent(routeSnapshot: ActivatedRouteSnapshot) {
    if (routeSnapshot.firstChild) {
      this.updateCurrentComponent(routeSnapshot.firstChild);
    } else if (routeSnapshot.component) {
      this.userContextService.actualPage = COMPONENT_TITLE[(routeSnapshot.component as any).name] || $localize`Inconnu`;
    }
  }

  /**
   * Enrolement notifications Firebase
   */
  private async enrollUserToFirebase() {
    await this.commonNotificationHandler.getToken();
    this.commonNotificationHandler
      .handleCommonNotifications()
      .pipe(
        catchError((error) => {
          this.logger.error(error, 'Subscribe notification error');
          return of(null);
        })
      )
      //Pas de gestion d'erreur sur cet appel : si on est en erreur la notification ne fera rien
      .subscribe(async (evt: any) => {
        if (evt) {
          if (evt.type === CrtToastCloseType.ACTION) {
            const evtData: NotificationCRT = evt.data;
            switch (evtData.sousType) {
              case CrtNotificationSousType.NEWS:
              case CrtNotificationSousType.INDISPOS: {
                const newsIndispo = await this.newsIndisposService.getLocalNewsIndispoById(
                  parseInt(evtData.idNewsIndispo)
                );
                if (newsIndispo) {
                  const modal = await this.modalCtrl.create({
                    component: NewsIndispoDetailComponent,
                    componentProps: {
                      newsIndispo: newsIndispo,
                    },
                  });
                  await modal.present();
                }
                break;
              }
              case CrtNotificationSousType.COMMUNICATION:
              default:
                if (this.hasCrtCommunicationNavigationData(evtData)) {
                  this.handleCrtCommunicationNotification(evtData);
                } else {
                  this.logger.warn('Notification communication CRT incomplète');
                }
                break;
            }
          } else if (
            evt.type === MultiTechNotification.DIEmergency ||
            evt.type === MultiTechNotification.DICascade ||
            evt.type === MultiTechNotification.DIUo
          ) {
            await this.router.navigate(['/mnt/ots/', evt.irId], {
              queryParams: {
                notificationEventName: evt.notificationEventName,
                notificationEstAstreinte: evt.notificationEstAstreinte,
              },
            });
          }
        } else if (evt.type === MultiTechNotification.STIEndStep) {
          await this.router.navigate(['/mnt/stis/', evt.idSti, 'details'], {
            queryParams: {
              idSti: evt.idSti,
              isEditable: true,
            },
          });
        }
      });
  }

  private hasCrtCommunicationNavigationData(evtData: NotificationCRT): boolean {
    return evtData.idSite !== undefined && evtData.idSite !== null && !!evtData.typeComm;
  }

  private handleCrtCommunicationNotification(evtData: NotificationCRT) {
    this.siteService.getSiteByIdSite(evtData.idSite, true).subscribe(async (site) => {
      await this.siteService.removeAllLastRequestDateForIdSite(site.identifiant);
      switch (evtData.typeComm) {
        case CrtTypeCommToast.COMMUNICATION_FLASH_PH:
          await this.gestionNotifCommPagePH(site, evtData);
          break;
        case CrtTypeCommToast.COMMUNICATION_FLASH_LR:
          await this.gestionNotifCommPageLR(site, evtData);
          break;
        case CrtTypeCommToast.COMMUNICATION_COMPLETE:
          await this.gestionNotifComCompleteLight(site);
          break;
        case CrtTypeCommToast.COMMUNICATION_LIGHT:
          await this.gestionNotifComCompleteLight(site);
          break;
        default:
          this.logger.warn('Type de communication inconnue');
      }
    });
  }

  /**
   * Gestion d'un notification de type communication page sur un ph
   * @param site site pour lequel arrive la notification
   * @param evt evenement de la notification
   */
  private async gestionNotifCommPagePH(site, evt: NotificationCRT) {
    if (evt.idMetier) {
      await this.goToSyntheticView(site);
      await this.navCtrl.navigateForward(['/crt/synthetic-view/lr-ph/crt-ph-page'], {
        state: {
          phId: +evt.idMetier,
          emplacementSite: site,
        },
      });
    } else {
      await this.goToSyntheticView(site);
    }
  }

  private async gestionNotifComCompleteLight(site) {
    await this.goToSyntheticView(site);
  }

  private async goToSyntheticView(site) {
    await this.navCtrl.navigateRoot('/crt');
    await this.navCtrl.navigateForward(['/crt/synthetic-view'], { state: { emplacementSite: site } });
  }

  /**
   * Gestion d'un notification de type communication page sur une LR
   * @param site site pour lequel arrive la notification
   * @param evt evenement de la notification
   */
  private async gestionNotifCommPageLR(site, evt: NotificationCRT) {
    if (evt.idMetier) {
      this.lrService
        .getLoiRegultationByIdWithIdSite(site.identifiant, +evt.idMetier)
        .pipe(
          take(1),
          catchError(async () => await this.goToSyntheticView(site))
        )
        .subscribe(async (lr) => {
          if (lr) {
            await this.goToSyntheticView(site);
            await this.navCtrl.navigateForward(['/crt/synthetic-view/lr-ph/lr-details'], {
              state: {
                lr: lr,
                emplacementSite: site,
              },
            });
          } else {
            await this.goToSyntheticView(site);
          }
        });
    } else {
      await this.goToSyntheticView(site);
    }
  }

  isLinkVisible(link: MenuItem) {
    if (link.action === MenuPage.ChangeUser && this.userService.isImpersonate()) {
      return true;
    }
    const listeProfils = this.userService.getProfils();
    return listeProfils.some((userProfil) => link.profils.indexOf(userProfil) >= 0);
  }

  async navigateTo(link: MenuItem) {
    this.menuService.desactive(link.title);
    switch (link.action) {
      case MenuPage.DeclareDysfonctionnement:
        await this.closeMenu();
        this.openDysfunctionnementModal();
        break;
      case MenuPage.ChangeUser:
        this.userService.logoutBackendUser();
        window.location.reload();
        break;
      default:
        this.userContextService.menuSelected = link.title;
        this.active = link.action;
        this.activeLink = link;
        this.updateDisabledMenuItem();
        this.navCtrl.navigateRoot(link.url);
    }
  }

  /**
   * Ouvre la modal de declaration de dysfonctionnement avec screenshot
   */
  async openDysfunctionnementModal() {
    let dysDeclarationModal;
    if (Capacitor.isNativePlatform()) {
      const photoInfo = await Screenshot.take();
      dysDeclarationModal = await this.modalCtrl.create({
        component: DysfonctionnementPage,
        componentProps: {
          photoInfo,
        },
      });
      await dysDeclarationModal.present();
    } else {
      dysDeclarationModal = await this.modalCtrl.create({
        component: DysfonctionnementPage,
      });
      await dysDeclarationModal.present();
    }
    dysDeclarationModal.onDidDismiss().then(() => {
      this.menuService.rollBackToPreviousMenu();
    });
  }

  /**
   * Fermer le menu, impossible avec le menu controller a cause de standalone https://github.com/ionic-team/ionic-framework/issues/28337
   */
  async closeMenu() {
    this.document_.getElementById('main-menu').click();
    await new Promise((r) => setTimeout(r, 300)); // nécessaire avec l'animation de fermeture du menu
  }

  /**
   * Mise à jour du statut disabled d'un item du menu en fonction de l'item actif
   */
  updateDisabledMenuItem() {
    // cas item menu declarer dysfonctionnement
    const pagesWithDysfunctionnementDeclaration = [MenuPage.CRT, MenuPage.IOT, MenuPage.Profil, MenuPage.NewsIndispo];
    let item = this.links.find((menuItem) => menuItem.action === MenuPage.DeclareDysfonctionnement);
    if (item) {
      item.disabled = !pagesWithDysfunctionnementDeclaration.includes(this.active) || this.userService.isImpersonate();
    }
    if (environment.desactivateCRTAndIOT) {
      const ecrtAndIotMenusToDisable = [
        MenuPage.CRT,
        MenuPage.IOT,
        MenuPage.NewsIndispo,
        MenuPage.DeclareDysfonctionnement,
      ];
      ecrtAndIotMenusToDisable.forEach((menuToDisable) => {
        let item: MenuItem = this.links.find((menuItem) => {
          return menuItem.action === menuToDisable;
        });
        if (item) {
          item.disabled = true;
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    if (this.dbCreatedSubscription) {
      this.dbCreatedSubscription.unsubscribe();
    }
  }

  private refreshTab() {
    let list_chronos = this.chronosProvider.getChronos();
    this.hasChrono.set(list_chronos !== null && list_chronos.length > 0);
  }

  private processViewChanged() {
    this.showChrono =
      this.active != MenuPage.DeclareDysfonctionnement &&
      !this.router.url.includes('cr/synthese') &&
      !this.router.url.includes('/mnt/activite-interne');

    this.authenticated = this.userService.isAuthenticated();
    if (this.authenticated) {
      this.isMntDiffus = this.userService.isMntProfil() || this.userService.isDiffusProfil();
    }
  }

  checkLinksDisabledNetwork(connected) {
    let aideIndex = this.links.findIndex((link) => {
      return link.action == MenuPage.ChangeUser;
    });

    if (aideIndex !== -1) {
      // On désactive le menu aide en offline
      this.links[aideIndex].disabled = !connected;
    }
  }

  protected readonly isToggleConsentGeolocEnabled = isToggleConsentGeolocEnabled;
  protected readonly isTogglePtiButtonSOSEnabled = isTogglePtiButtonSOSEnabled;
}
